// Feature: docs/features/nma-demo-dashboard.md
import React from 'react';
import {
  Box, Flex, Text, Badge, Heading, Container,
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Card, CardHeader, CardBody, HStack, VStack, Select,
  Grid, GridItem, Progress, Divider
} from "@chakra-ui/react";
import HeaderMenus from './HeaderMenus';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine 
} from 'recharts';

export default function NMADemoDashboard() {
  const denialTrendData = [
    { week: 'W1', UHC: 6.8, Aetna: 5.9, Cigna: 4.2, Anthem: 5.1, Medicare: 2.1 },
    { week: 'W2', UHC: 7.1, Aetna: 6.1, Cigna: 4.5, Anthem: 4.9, Medicare: 2.2 },
    { week: 'W3', UHC: 6.9, Aetna: 6.0, Cigna: 4.3, Anthem: 5.2, Medicare: 2.0 },
    { week: 'W4', UHC: 7.2, Aetna: 5.8, Cigna: 4.6, Anthem: 5.0, Medicare: 2.1 },
    { week: 'W5', UHC: 7.0, Aetna: 6.2, Cigna: 4.4, Anthem: 5.3, Medicare: 2.3 },
    { week: 'W6', UHC: 7.3, Aetna: 6.1, Cigna: 4.7, Anthem: 5.1, Medicare: 2.2 },
    { week: 'W7', UHC: 7.1, Aetna: 8.5, Cigna: 4.5, Anthem: 4.8, Medicare: 2.1 },
    { week: 'W8', UHC: 7.4, Aetna: 8.6, Cigna: 4.8, Anthem: 5.0, Medicare: 2.0 },
    { week: 'W9', UHC: 12.4, Aetna: 8.4, Cigna: 4.6, Anthem: 5.2, Medicare: 2.2 },
    { week: 'W10', UHC: 12.6, Aetna: 8.5, Cigna: 4.9, Anthem: 4.9, Medicare: 2.1 },
    { week: 'W11', UHC: 12.5, UHC_Projected: 12.5, Aetna: 8.6, Aetna_Projected: 8.6, Cigna: 4.7, Anthem: 5.1, Medicare: 2.3 },
    { week: 'W12', UHC: null, UHC_Projected: 9.2, Aetna: null, Aetna_Projected: 7.1, Cigna: 4.5, Anthem: 5.0, Medicare: 2.2 },
    { week: 'W13', UHC: null, UHC_Projected: 7.5, Aetna: null, Aetna_Projected: 6.3, Cigna: 4.8, Anthem: 5.2, Medicare: 2.1 },
  ];

  // Custom styling components to match the high-fidelity mockup
  const CodeChip = ({ children, isHot }) => (
    <Badge 
      fontFamily="mono" 
      fontSize="xs" 
      fontWeight="medium" 
      bg={isHot ? "red.50" : "gray.100"} 
      color={isHot ? "red.600" : "gray.700"}
      border="1px solid"
      borderColor={isHot ? "red.200" : "gray.200"}
      px={1.5} 
      py={0.5} 
      rounded="md"
      textTransform="none"
    >
      {children}
    </Badge>
  );

  const Trend = ({ direction, value }) => {
    const isUp = direction === 'up';
    const isDown = direction === 'down';
    const color = isUp ? "red.500" : isDown ? "green.500" : "gray.500";
    const icon = isUp ? "▲" : isDown ? "▼" : "—";
    return (
      <Text as="span" fontFamily="mono" fontSize="xs" fontWeight="bold" color={color}>
        {icon} {value}
      </Text>
    );
  };

  const KPICard = ({ label, value, delta, deltaType, meta }) => {
    const isGood = deltaType === 'good';
    const isBad = deltaType === 'bad';
    const deltaBg = isGood ? "green.50" : isBad ? "red.50" : "gray.100";
    const deltaColor = isGood ? "green.600" : isBad ? "red.600" : "gray.600";

    return (
      <Card shadow="sm" borderWidth="1px" borderColor="gray.200">
        <CardBody p={3}>
          <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
            {label}
          </Text>
          <Flex align="baseline" mt={1} mb={1}>
            <Text fontSize="xl" fontWeight="bold" fontFamily="mono" letterSpacing="tight">
              {value}
            </Text>
            <Badge ml={2} bg={deltaBg} color={deltaColor} fontSize="10px" rounded="full" px={2} textTransform="none">
              {delta}
            </Badge>
          </Flex>
          <Text fontSize="10px" color="gray.400">{meta}</Text>
        </CardBody>
      </Card>
    );
  };

  return (
    <Box minH="100vh" bg="gray.50" pb={12}>
      <HeaderMenus />
      
      {/* Top Chrome / Brand Bar */}
      <Flex bg="white" borderBottomWidth="1px" borderColor="gray.200" px={6} py={3} align="center" justify="space-between" position="sticky" top={0} zIndex={50}>
        <HStack spacing={3}>
          <Flex align="center" justify="center" w="26px" h="26px" rounded="md" bgGradient="linear(to-br, teal.600, teal.800)" color="white" fontFamily="mono" fontSize="xs" fontWeight="bold">
            Rx
          </Flex>
          <Text fontWeight="bold" fontSize="md" color="gray.800">
            RevCycle IQ <Text as="span" color="gray.500" fontWeight="medium">· Neuromonitoring Associates</Text>
          </Text>
        </HStack>
        <HStack spacing={6} display={{ base: "none", lg: "flex" }}>
          <Text fontSize="sm" fontWeight="bold" color="teal.800" bg="teal.50" px={3} py={1.5} rounded="md">Command Center</Text>
          <Text fontSize="sm" fontWeight="medium" color="gray.500" _hover={{ color: "gray.800", cursor: "pointer" }}>Denials & Appeals</Text>
          <Text fontSize="sm" fontWeight="medium" color="gray.500" _hover={{ color: "gray.800", cursor: "pointer" }}>Payer Intelligence</Text>
          <Text fontSize="sm" fontWeight="medium" color="gray.500" _hover={{ color: "gray.800", cursor: "pointer" }}>Contract Variance</Text>
        </HStack>
        <Box textAlign="right" display={{ base: "none", md: "block" }}>
          <Text fontSize="xs" color="gray.500">
            <Text as="span" display="inline-block" w="6px" h="6px" rounded="full" bg="green.500" mr={1.5}></Text>
            <Text as="span" fontWeight="bold" color="gray.700">Live</Text> · Last 835 ERA batch parsed 11:42 AM PT
          </Text>
          <Text fontSize="xs" color="gray.500">277CA acks current · EOB OCR queue: 0 pending</Text>
        </Box>
      </Flex>

      {/* Live Ticker */}
      <Flex bg="gray.900" color="blue.100" px={6} py={2} fontSize="xs" overflow="hidden" whiteSpace="nowrap" gap={8}>
        <HStack>
          <Badge bg="red.800" color="red.100" fontSize="2xs">DRIFT</Badge>
          <Text><b>UnitedHealthcare</b> · CARC 204 rate on 95941 increased to 88% — detected 11:06 AM</Text>
        </HStack>
        <HStack>
          <Badge bg="orange.800" color="orange.100" fontSize="2xs">POLICY</Badge>
          <Text><b>Aetna</b> · CO-252 on 95870 w/ M54.16 — medical necessity criteria updated — detected 09:31 AM</Text>
        </HStack>
        <HStack>
          <Badge bg="orange.800" color="orange.100" fontSize="2xs">EDIT</Badge>
          <Text><b>Cigna</b> · 95999 (TOF) returning CARC 204 — clinical justification packet required at submission</Text>
        </HStack>
      </Flex>

      <Container maxW="container.xl" pt={6}>
        
        {/* Page Header */}
        <Flex justify="space-between" align="flex-end" mb={6} wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="gray.800" letterSpacing="tight">Revenue Cycle Command Center</Heading>
            <Text color="gray.500" fontSize="sm" mt={1}>CMS-1500 professional claims · 42 providers · Intraoperative Neuromonitoring · Trailing 90 days vs. prior period</Text>
          </Box>
          <HStack>
            <Select size="sm" bg="white" w="160px" defaultValue="90">
              <option value="90">Trailing 90 days</option>
              <option value="30">Trailing 30 days</option>
              <option value="mtd">MTD</option>
            </Select>
            <Select size="sm" bg="white" w="160px" defaultValue="all">
              <option value="all">All payers (23)</option>
              <option value="uhc">UnitedHealthcare</option>
              <option value="aetna">Aetna</option>
              <option value="cigna">Cigna</option>
            </Select>
            <Button size="sm" variant="outline" bg="white">Export</Button>
            <Button size="sm" colorScheme="teal">Run analysis now</Button>
          </HStack>
        </Flex>

        {/* KPI Strip */}
        <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(7, 1fr)" }} gap={4} mb={6}>
          <KPICard label="First-Pass Clean" value="94.2%" delta="▲ 0.8 pp" deltaType="good" meta="837P accepted w/o edit" />
          <KPICard label="Initial Denial Rate" value="8.7%" delta="▼ 1.2 pp" deltaType="good" meta="By first CARC on 835" />
          <KPICard label="$ in Denied A/R" value="$x.xM" delta="▼ $xxxK" deltaType="good" meta="Open denials, net of adj" />
          <KPICard label="Appeal Overturn Rate" value="71.4%" delta="▲ 4.1 pp" deltaType="good" meta="Level 1, auto-generated" />
          <KPICard label="Days in A/R" value="34.1" delta="▼ 2.6" deltaType="good" meta="Net; >90-day at 14.8%" />
          <KPICard label="Denial → Appeal Filed" value="1.9 d" delta="▼ 6.2 d" deltaType="good" meta="Median; was 8.1 d pre-auto" />
          <KPICard label="Net Collection Rate" value="96.8%" delta="— 0.0" deltaType="neutral" meta="vs. expected allowed" />
        </Grid>

        {/* Main Content Grid */}
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={6}>
          
          {/* Left Column: Drift Detection */}
          <GridItem>
            <Card shadow="sm" borderWidth="1px" borderColor="gray.200" h="100%">
              <CardHeader bg="white" borderBottomWidth="1px" borderColor="gray.100" py={3}>
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading size="sm" color="gray.800">Payer Behavior Drift — Active Detections</Heading>
                    <Text fontSize="xs" color="gray.500" mt={1}>Statistical change-point detection on 835/277CA streams & payer bulletins</Text>
                  </Box>
                  <HStack>
                    <Badge colorScheme="red">1 critical</Badge>
                    <Badge colorScheme="orange">2 high</Badge>
                  </HStack>
                </Flex>
              </CardHeader>
              <CardBody p={0}>
                <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.100" />}>
                  
                  {/* Alert 1: UHC 95941 */}
                  <Box p={4} borderLeftWidth="4px" borderLeftColor="red.500" bg="white">
                    <Flex justify="space-between" align="flex-start" gap={4}>
                      <Box>
                        <HStack mb={2}>
                          <Badge colorScheme="red" variant="subtle" fontSize="2xs">Critical · Policy Adoption</Badge>
                          <Text fontWeight="bold" fontSize="sm" color="gray.800">UnitedHealthcare — Commercial adoption of Medicare remote monitoring rules</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600" lineHeight="tall">
                          <CodeChip isHot>CARC 204</CodeChip> (Not covered) applied to <CodeChip>95941</CodeChip> claim lines. 
                          Denial rate increased from <Text as="span" fontWeight="bold" color="gray.800">2.1% baseline → 88.0%</Text> effective <Text as="span" fontWeight="bold" color="gray.800">07/14</Text>. 
                          Pattern indicates silent adoption of CMS guidelines rejecting the commercial code.
                        </Text>
                        <Box mt={3} p={2} bg="teal.50" rounded="md" borderWidth="1px" borderColor="teal.100">
                          <Text fontSize="xs" color="teal.900">
                            <Text as="span" fontWeight="bold">Auto-rule drafted:</Text> Route claims to HCPCS <CodeChip>G0453</CodeChip> pending a time-documentation check in the operative report. Projected revenue protected: ~$xxx/hr.
                          </Text>
                        </Box>
                        <HStack mt={3}>
                          <Button size="xs" colorScheme="teal">Test G0453 crosswalk (10%)</Button>
                          <Button size="xs" variant="outline">See 47 affected claims</Button>
                        </HStack>
                      </Box>
                      <Box textAlign="right" minW="100px">
                        <Text fontFamily="mono" fontWeight="bold" fontSize="lg" color="gray.800">$xxx,xxx</Text>
                        <Text fontSize="xs" color="gray.500">47 claims · since 07/14</Text>
                      </Box>
                    </Flex>
                  </Box>

                  {/* Alert 2: Aetna 95870 */}
                  <Box p={4} borderLeftWidth="4px" borderLeftColor="orange.500" bg="white">
                    <Flex justify="space-between" align="flex-start" gap={4}>
                      <Box>
                        <HStack mb={2}>
                          <Badge colorScheme="orange" variant="subtle" fontSize="2xs">High · Scrubber Update</Badge>
                          <Text fontWeight="bold" fontSize="sm" color="gray.800">Aetna — Medical necessity criteria updated for muscle testing (95870)</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600" lineHeight="tall">
                          Standard radiculopathy diagnoses (e.g., <CodeChip>M54.16</CodeChip>) are resulting in automated <CodeChip isHot>CO-252</CodeChip> (Request for Medical Records) denials. 
                          Payer has updated acceptable diagnosis criteria.
                        </Text>
                        <Box mt={3} p={2} bg="teal.50" rounded="md" borderWidth="1px" borderColor="teal.100">
                          <Text fontSize="xs" color="teal.900">
                            <Text as="span" fontWeight="bold">CDI rule drafted:</Text> Scan op notes for documented high-acuity symptoms (e.g., <CodeChip>R20.2</CodeChip>, <CodeChip>M51.04</CodeChip>) and prompt coders to append before submission.
                          </Text>
                        </Box>
                        <HStack mt={3}>
                          <Button size="xs" colorScheme="teal">Activate pre-bill CDI prompt</Button>
                          <Button size="xs" variant="outline">See 31 affected claims</Button>
                        </HStack>
                      </Box>
                      <Box textAlign="right" minW="100px">
                        <Text fontFamily="mono" fontWeight="bold" fontSize="lg" color="gray.800">$xxx,xxx</Text>
                        <Text fontSize="xs" color="gray.500">31 claims · since 07/03</Text>
                      </Box>
                    </Flex>
                  </Box>

                  {/* Alert 3: Cigna TOF */}
                  <Box p={4} borderLeftWidth="4px" borderLeftColor="orange.500" bg="white">
                    <Flex justify="space-between" align="flex-start" gap={4}>
                      <Box>
                        <HStack mb={2}>
                          <Badge colorScheme="orange" variant="subtle" fontSize="2xs">High · Edit Change</Badge>
                          <Text fontWeight="bold" fontSize="sm" color="gray.800">Cigna — Front-end rejections for Train of Four (95999)</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600" lineHeight="tall">
                          Historically, <CodeChip>95999</CodeChip> (TOF) proceeded to manual review. Claims are now returning <CodeChip isHot>CARC 204</CodeChip> (Not covered) without prior request for records.
                        </Text>
                        <Box mt={3} p={2} bg="teal.50" rounded="md" borderWidth="1px" borderColor="teal.100">
                          <Text fontSize="xs" color="teal.900">
                            <Text as="span" fontWeight="bold">Preventive action queued:</Text> Front-load claim with standardized Train of Four clinical justification packet and literature attachments upon initial submission to force manual review.
                          </Text>
                        </Box>
                        <HStack mt={3}>
                          <Button size="xs" colorScheme="teal">Auto-attach TOF packet</Button>
                          <Button size="xs" variant="outline">View 22 scheduled cases</Button>
                        </HStack>
                      </Box>
                      <Box textAlign="right" minW="100px">
                        <Text fontFamily="mono" fontWeight="bold" fontSize="lg" color="gray.800">$xxx,xxx</Text>
                        <Text fontSize="xs" color="gray.500">22 scheduled cases at risk</Text>
                      </Box>
                    </Flex>
                  </Box>

                </VStack>
              </CardBody>
            </Card>
          </GridItem>

          {/* Right Column: Pipelines & Queues */}
          <GridItem>
            <VStack align="stretch" spacing={6} h="100%">
              
              <Card shadow="sm" borderWidth="1px" borderColor="gray.200">
                <CardHeader bg="white" borderBottomWidth="1px" borderColor="gray.100" py={3}>
                  <Heading size="sm" color="gray.800">Ingestion & Automation — Today</Heading>
                </CardHeader>
                <CardBody py={2} px={4}>
                  <VStack align="stretch" spacing={2} divider={<Divider borderColor="gray.100" borderStyle="dashed" />}>
                    <Flex justify="space-between" fontSize="sm"><Text color="gray.500">835 ERA files parsed</Text><Text fontFamily="mono" fontWeight="bold">142 <Text as="span" color="green.500">· 100%</Text></Text></Flex>
                    <Flex justify="space-between" fontSize="sm"><Text color="gray.500">Paper EOBs OCR'd → posted</Text><Text fontFamily="mono" fontWeight="bold">67 <Text as="span" color="green.500">· 99.1% conf.</Text></Text></Flex>
                    <Flex justify="space-between" fontSize="sm"><Text color="gray.500">277CA acknowledgments reconciled</Text><Text fontFamily="mono" fontWeight="bold">1,204</Text></Flex>
                    <Flex justify="space-between" fontSize="sm"><Text color="gray.500">Corrected claims generated (837P)</Text><Text fontFamily="mono" fontWeight="bold">96</Text></Flex>
                    <Flex justify="space-between" fontSize="sm"><Text color="gray.500">Appeal packets generated</Text><Text fontFamily="mono" fontWeight="bold">38 <Text as="span" color="orange.500">· 9 await review</Text></Text></Flex>
                    <Flex justify="space-between" fontSize="sm"><Text color="gray.500">PA requirements verified pre-service</Text><Text fontFamily="mono" fontWeight="bold">54</Text></Flex>
                  </VStack>
                </CardBody>
              </Card>

              <Card shadow="sm" borderWidth="1px" borderColor="gray.200">
                <CardHeader bg="white" borderBottomWidth="1px" borderColor="gray.100" py={3}>
                  <Flex justify="space-between" align="center">
                    <Heading size="sm" color="gray.800">Work Queues</Heading>
                    <Text fontSize="xs" color="teal.600" fontWeight="bold" cursor="pointer">Open queues →</Text>
                  </Flex>
                </CardHeader>
                <CardBody py={2} px={4}>
                  <VStack align="stretch" spacing={2} divider={<Divider borderColor="gray.100" borderStyle="dashed" />}>
                    <Flex justify="space-between" fontSize="sm" align="center">
                      <Text color="gray.700">Appeals — pending human review <Badge colorScheme="orange" ml={1} fontSize="2xs">SLA 24h</Badge></Text>
                      <Text fontFamily="mono" fontWeight="bold">9</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" align="center">
                      <Text color="gray.700">Timely filing &lt; 14 days remaining</Text>
                      <Text fontFamily="mono" fontWeight="bold" color="red.500">6</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" align="center">
                      <Text color="gray.700">Redeterminations awaiting payer response</Text>
                      <Text fontFamily="mono" fontWeight="bold">114</Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="sm" align="center">
                      <Text color="gray.700">Peer-to-peer scheduling required (CO-50)</Text>
                      <Text fontFamily="mono" fontWeight="bold">4</Text>
                    </Flex>
                  </VStack>
                </CardBody>
              </Card>

            </VStack>
          </GridItem>
        </Grid>

        {/* Middle Row: Trend Chart & Top Denials */}
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={6}>
          
          <GridItem>
            <Card shadow="sm" borderWidth="1px" borderColor="gray.200" h="100%">
              <CardHeader bg="white" borderBottomWidth="1px" borderColor="gray.100" py={3}>
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading size="sm" color="gray.800">Denial Rate Trend — with Detected Change Points</Heading>
                    <Text fontSize="xs" color="gray.500" mt={1}>Weekly initial denial rate ($-weighted), by top 5 payers</Text>
                  </Box>
                  <HStack>
                    <Button size="xs" variant="outline" bg="gray.50">$-weighted</Button>
                    <Button size="xs" variant="ghost" color="gray.500">Count-weighted</Button>
                  </HStack>
                </Flex>
              </CardHeader>
              <CardBody>
                <Box height="320px" w="100%" pt={4}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={denialTrendData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#718096' }} axisLine={{ stroke: '#E2E8F0' }} tickLine={false} />
                      <YAxis 
                        domain={[0, 16]} 
                        tickFormatter={(val) => `${val}%`} 
                        tick={{ fontSize: 12, fill: '#718096' }} 
                        axisLine={false} 
                        tickLine={false} 
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          const cleanName = name.replace('_Projected', ' (Projected)');
                          return [`${value}%`, cleanName];
                        }}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      
                      <ReferenceLine x="W9" stroke="#E53E3E" strokeDasharray="3 3" label={{ position: 'top', value: 'Drift: CARC 204 (95941)', fill: '#E53E3E', fontSize: 10, fontWeight: 'bold' }} />
                      <ReferenceLine x="W7" stroke="#DD6B20" strokeDasharray="3 3" label={{ position: 'top', value: 'Scrubber: CO-252 (95870)', fill: '#DD6B20', fontSize: 10, fontWeight: 'bold' }} />

                      <Line type="monotone" dataKey="UHC" stroke="#E53E3E" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="UHC_Projected" name="UHC (Projected)" stroke="#E53E3E" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      
                      <Line type="monotone" dataKey="Aetna" stroke="#DD6B20" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="Aetna_Projected" name="Aetna (Projected)" stroke="#DD6B20" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      
                      <Line type="monotone" dataKey="Cigna" stroke="#3182CE" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Anthem" stroke="#805AD5" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Medicare" stroke="#718096" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card shadow="sm" borderWidth="1px" borderColor="gray.200" h="100%">
              <CardHeader bg="white" borderBottomWidth="1px" borderColor="gray.100" py={3}>
                <Heading size="sm" color="gray.800">Top Denial Reasons — 30 Days</Heading>
                <Text fontSize="xs" color="gray.500" mt={1}>By adjustment group / CARC · $-weighted</Text>
              </CardHeader>
              <CardBody p={0}>
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontSize="2xs" color="gray.500">CARC / RARC</Th>
                      <Th fontSize="2xs" color="gray.500" isNumeric>$ Denied</Th>
                      <Th fontSize="2xs" color="gray.500" isNumeric>Trend</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800"><CodeChip isHot>CARC 204</CodeChip> Not covered</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>Driver: UHC 95941 & Cigna 95999</Text>
                        <Progress value={88} size="xs" colorScheme="red" mt={2} rounded="full" bg="gray.100" />
                      </Td>
                      <Td isNumeric fontFamily="mono" fontWeight="medium">$xxxK</Td>
                      <Td isNumeric><Trend direction="up" value="212%" /></Td>
                    </Tr>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800"><CodeChip isHot>CO-252</CodeChip> Docs requested</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>Driver: Aetna 95870 cohort</Text>
                        <Progress value={64} size="xs" colorScheme="orange" mt={2} rounded="full" bg="gray.100" />
                      </Td>
                      <Td isNumeric fontFamily="mono" fontWeight="medium">$xxxK</Td>
                      <Td isNumeric><Trend direction="up" value="38%" /></Td>
                    </Tr>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800"><CodeChip>CO-97</CodeChip> Bundled — NCCI PTP</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>95870 w/ 95861; XU remap in flight</Text>
                        <Progress value={47} size="xs" colorScheme="teal" mt={2} rounded="full" bg="gray.100" />
                      </Td>
                      <Td isNumeric fontFamily="mono" fontWeight="medium">$xxxK</Td>
                      <Td isNumeric><Trend direction="flat" value="2%" /></Td>
                    </Tr>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800"><CodeChip>CO-50</CodeChip> Not med. necessary</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>LCD criteria; 71% overturned w/ logs</Text>
                        <Progress value={41} size="xs" colorScheme="teal" mt={2} rounded="full" bg="gray.100" />
                      </Td>
                      <Td isNumeric fontFamily="mono" fontWeight="medium">$xxxK</Td>
                      <Td isNumeric><Trend direction="down" value="19%" /></Td>
                    </Tr>
                  </Tbody>
                </Table>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>

        {/* Bottom Row: Modifier Intel & Contract Variance */}
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} mb={6}>
          
          <GridItem>
            <Card shadow="sm" borderWidth="1px" borderColor="gray.200" h="100%">
              <CardHeader bg="white" borderBottomWidth="1px" borderColor="gray.100" py={3}>
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading size="sm" color="gray.800">Modifier Intelligence</Heading>
                    <Text fontSize="xs" color="gray.500" mt={1}>Denial & overturn behavior by modifier × payer</Text>
                  </Box>
                  <Text fontSize="xs" color="teal.600" fontWeight="bold" cursor="pointer">Full matrix →</Text>
                </Flex>
              </CardHeader>
              <CardBody p={0}>
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontSize="2xs" color="gray.500">Modifier / Context</Th>
                      <Th fontSize="2xs" color="gray.500">Payer</Th>
                      <Th fontSize="2xs" color="gray.500" isNumeric>Denial Rate</Th>
                      <Th fontSize="2xs" color="gray.500">Recommended Handling</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800"><CodeChip>59</CodeChip> → <CodeChip>XU</CodeChip> Distinct service</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>95870 / 95861 pair</Text>
                      </Td>
                      <Td fontSize="sm">Aetna</Td>
                      <Td isNumeric>
                        <Text fontFamily="mono" fontSize="sm">31.2%</Text>
                        <Trend direction="up" value="5.1×" />
                      </Td>
                      <Td fontSize="xs" color="gray.700" whiteSpace="normal">Use XU when op note documents separate muscle groups; 59 no longer bypasses PTP</Td>
                    </Tr>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800"><CodeChip>22</CodeChip> Increased procedural</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>Complex deformity cases</Text>
                      </Td>
                      <Td fontSize="sm">All commercial</Td>
                      <Td isNumeric>
                        <Text fontFamily="mono" fontSize="sm">—</Text>
                        <Trend direction="flat" value="n/a" />
                      </Td>
                      <Td fontSize="xs" color="gray.700" whiteSpace="normal">Uplift paid 64% when quantified time/complexity statement attached vs. 21% without</Td>
                    </Tr>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800"><CodeChip>51</CodeChip> Multiple procedure</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>Ranking check on 3+ lines</Text>
                      </Td>
                      <Td fontSize="sm">Anthem BCBS</Td>
                      <Td isNumeric>
                        <Text fontFamily="mono" fontSize="sm">2.1%</Text>
                        <Trend direction="down" value="0.4×" />
                      </Td>
                      <Td fontSize="xs" color="gray.700" whiteSpace="normal">Auto-ordering lines by RVU before submission; variance re-priced on remit</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem>
            <Card shadow="sm" borderWidth="1px" borderColor="gray.200" h="100%">
              <CardHeader bg="white" borderBottomWidth="1px" borderColor="gray.100" py={3}>
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading size="sm" color="gray.800">Contract Variance & Underpayments</Heading>
                    <Text fontSize="xs" color="gray.500" mt={1}>835 paid vs. contracted allowed, fee-schedule aware</Text>
                  </Box>
                  <Text fontSize="xs" color="teal.600" fontWeight="bold" cursor="pointer">All variances →</Text>
                </Flex>
              </CardHeader>
              <CardBody p={0}>
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontSize="2xs" color="gray.500">Payer · Issue</Th>
                      <Th fontSize="2xs" color="gray.500" isNumeric>Expected</Th>
                      <Th fontSize="2xs" color="gray.500" isNumeric>Paid</Th>
                      <Th fontSize="2xs" color="gray.500" isNumeric>Variance</Th>
                      <Th fontSize="2xs" color="gray.500">Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800">Anthem BCBS — <CodeChip>95940</CodeChip> stale fee schedule</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>14 remits since 07/21 · pattern, not one-off</Text>
                      </Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm">$x,xxx</Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm">$x,xxx</Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm" fontWeight="bold" color="red.600">−$xx.xK</Td>
                      <Td><Badge colorScheme="orange">Project opened</Badge></Td>
                    </Tr>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800">UnitedHealthcare — MPPR applied to add-ons</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>Add-ons exempt per contract · <CodeChip>95941</CodeChip></Text>
                      </Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm">$x,xxx</Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm">$x,xxx</Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm" fontWeight="bold" color="red.600">−$xx.xK</Td>
                      <Td><Badge colorScheme="blue">Reconsiderations filed</Badge></Td>
                    </Tr>
                    <Tr _hover={{ bg: "gray.50" }}>
                      <Td py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800">Aetna — sequestration-style 2% reduction</Text>
                        <Text fontSize="xs" color="gray.500" mt={0.5}>Not contractually supported · 41 remits</Text>
                      </Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm">—</Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm">—</Td>
                      <Td isNumeric fontFamily="mono" fontSize="sm" fontWeight="bold" color="red.600">−$xx.xK</Td>
                      <Td><Badge colorScheme="green">Recovered $xx.xK</Badge></Td>
                    </Tr>
                  </Tbody>
                </Table>
                <Box p={3} borderTopWidth="1px" borderColor="gray.100" bg="gray.50" fontSize="xs" color="gray.500">
                  Identified underpayments YTD: <Text as="span" fontFamily="mono" fontWeight="bold" color="gray.800">$x.xxM</Text> · 
                  Recovered: <Text as="span" fontFamily="mono" fontWeight="bold" color="green.600">$x.xxM</Text> · 
                  Recovery rate 65.2%
                </Box>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>

        {/* Full Width: Generated Actions */}
        <Card shadow="sm" borderWidth="1px" borderColor="gray.200">
          <CardHeader bg="white" borderBottomWidth="1px" borderColor="gray.100" py={3}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
              <Box>
                <Heading size="sm" color="gray.800">Generated Actions — Appeals & Corrected Claims</Heading>
                <Text fontSize="xs" color="gray.500" mt={1}>Drafted from EOB/835 denial data + clinical documentation · human review required above $10K or below 85% confidence</Text>
              </Box>
              <HStack>
                <Select size="sm" bg="white" w="180px" defaultValue="all">
                  <option value="all">All action types</option>
                  <option value="corrected">Corrected claim (837P)</option>
                  <option value="appeal">Appeal — Level 1</option>
                </Select>
                <Button size="sm" colorScheme="teal">Approve all high-confidence (12)</Button>
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody p={0} overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th fontSize="2xs" color="gray.500">Claim</Th>
                  <Th fontSize="2xs" color="gray.500">Payer · DOS</Th>
                  <Th fontSize="2xs" color="gray.500">Procedure(s)</Th>
                  <Th fontSize="2xs" color="gray.500">Denial</Th>
                  <Th fontSize="2xs" color="gray.500" isNumeric>Billed</Th>
                  <Th fontSize="2xs" color="gray.500">Generated Action</Th>
                  <Th fontSize="2xs" color="gray.500" isNumeric>Conf.</Th>
                  <Th fontSize="2xs" color="gray.500">Timely Filing</Th>
                  <Th fontSize="2xs" color="gray.500">Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td py={3}>
                    <Text fontFamily="mono" fontSize="sm" color="gray.800">CLM-2026-084112</Text>
                    <Text fontSize="xs" color="gray.500">Pt ●●●●●-4471</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.800">UnitedHealthcare</Text>
                    <Text fontSize="xs" color="gray.500">06/30/2026</Text>
                  </Td>
                  <Td>
                    <HStack spacing={1} mb={1}>
                      <CodeChip>95941</CodeChip>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">Continuous monitoring</Text>
                  </Td>
                  <Td>
                    <CodeChip isHot>CARC 204</CodeChip>
                    <Text fontSize="xs" color="gray.500" mt={1}>Not covered</Text>
                  </Td>
                  <Td isNumeric fontFamily="mono" fontSize="sm">$x,xxx</Td>
                  <Td fontSize="xs" color="gray.700" whiteSpace="normal" maxW="300px">
                    <Text as="span" fontWeight="bold">Corrected claim (837P)</Text> — Crosswalk to G0453 applied after NLP confirmed continuous monitoring time &gt; 15 mins.
                  </Td>
                  <Td isNumeric fontFamily="mono" fontSize="sm">96%</Td>
                  <Td fontFamily="mono" fontSize="sm" fontWeight="bold" color="orange.500">41 d</Td>
                  <Td><Badge colorScheme="orange">Awaiting review</Badge></Td>
                </Tr>
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td py={3}>
                    <Text fontFamily="mono" fontSize="sm" color="gray.800">CLM-2026-083987</Text>
                    <Text fontSize="xs" color="gray.500">Pt ●●●●●-2210</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.800">Aetna</Text>
                    <Text fontSize="xs" color="gray.500">06/24/2026</Text>
                  </Td>
                  <Td>
                    <HStack spacing={1} mb={1}>
                      <CodeChip>95870</CodeChip>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">Muscle testing</Text>
                  </Td>
                  <Td>
                    <CodeChip isHot>CO-252</CodeChip>
                    <Text fontSize="xs" color="gray.500" mt={1}>Docs requested</Text>
                  </Td>
                  <Td isNumeric fontFamily="mono" fontSize="sm">$x,xxx</Td>
                  <Td fontSize="xs" color="gray.700" whiteSpace="normal" maxW="300px">
                    <Text as="span" fontWeight="bold">Pre-bill CDI Hold</Text> — Op note indicates R20.2 (Paresthesia); awaiting coder approval to append diagnosis.
                  </Td>
                  <Td isNumeric fontFamily="mono" fontSize="sm">93%</Td>
                  <Td fontFamily="mono" fontSize="sm">78 d</Td>
                  <Td><Badge colorScheme="green">Ready to submit</Badge></Td>
                </Tr>
                <Tr _hover={{ bg: "gray.50" }}>
                  <Td py={3}>
                    <Text fontFamily="mono" fontSize="sm" color="gray.800">CLM-2026-083655</Text>
                    <Text fontSize="xs" color="gray.500">Pt ●●●●●-9917</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.800">Cigna</Text>
                    <Text fontSize="xs" color="gray.500">06/15/2026</Text>
                  </Td>
                  <Td>
                    <HStack spacing={1} mb={1}>
                      <CodeChip>95999</CodeChip>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">Train of Four (TOF)</Text>
                  </Td>
                  <Td>
                    <CodeChip isHot>CARC 204</CodeChip>
                    <Text fontSize="xs" color="gray.500" mt={1}>Not covered</Text>
                  </Td>
                  <Td isNumeric fontFamily="mono" fontSize="sm">$xxx</Td>
                  <Td fontSize="xs" color="gray.700" whiteSpace="normal" maxW="300px">
                    <Text as="span" fontWeight="bold">Appeal L1</Text> — Standardized Train of Four clinical justification packet attached.
                  </Td>
                  <Td isNumeric fontFamily="mono" fontSize="sm">81%</Td>
                  <Td fontFamily="mono" fontSize="sm" fontWeight="bold" color="red.500">12 d</Td>
                  <Td><Badge colorScheme="red">Review — low conf.</Badge></Td>
                </Tr>
              </Tbody>
            </Table>
            <Flex justify="space-between" align="center" p={3} borderTopWidth="1px" borderColor="gray.100" bg="gray.50" fontSize="xs" color="gray.500">
              <Text>Showing 3 of 143 generated actions · sorted by timely-filing risk</Text>
              <Text>Last 30 days: <Text as="span" fontWeight="bold" color="gray.800">412 actions generated</Text> · 71.4% overturn rate · <Text as="span" fontFamily="mono" fontWeight="bold" color="green.600">$x.xxM recovered</Text></Text>
            </Flex>
          </CardBody>
        </Card>

        <Text fontSize="xs" color="gray.400" mt={8} lineHeight="tall">
          Demo environment — all patient identifiers masked; claim data is synthetic and representative. Sources: 835 ERA, 837P submission history, 277CA acknowledgments, scanned EOB (OCR), payer bulletins & medical policy pages (monitored daily), NCCI PTP/MUE tables (quarterly), practice fee schedules & contract terms. Baselines computed per payer × CPT × modifier cohort over trailing 90 days; drift flagged at ≥99% change-point confidence.
        </Text>

      </Container>
    </Box>
  );
}
